import moment from 'moment'
import {
  AADocumentMetaData,
  AAFacilityDetails,
} from 'app/models/aa-metadata-params'
import { FacilityParams } from 'app/models/facility-params'
import { TradedFacilityParams } from 'app/models/traded-facilities-params'

/**
 * Detects AA documents whose amounts no longer match the trade.
 *
 * DAML writes each AA facility row's `currentAmount` from the traded facility's
 * latest amount entry — `optional tf.tradeAmount (\a -> a.amount) (last tf.amounts)`
 * — both when the document is first generated and every time it is refreshed
 * (MasterTradeActions.UpdateAADocumentCurrentAmount archives the metadata and
 * recreates it with fresh amounts). So the two agree exactly as long as
 * refreshes keep succeeding, and drift apart the moment one fails.
 *
 * That makes the comparison a direct read of "this document was not refreshed",
 * derived from data the portal already loads — unlike a toast, it survives a
 * reload and can be filtered on.
 */

/** Currency amounts are decimal strings; treat a sub-cent gap as equal. */
const AMOUNT_EPSILON = 0.005

const normalize = (value: string | null | undefined) =>
  (value ?? '').trim().toLowerCase()

/**
 * The trade's live current amount for a facility — the same derivation DAML
 * uses when it writes the document, so the two are comparable.
 */
export const currentTradedFacilityAmount = (
  tradedFacility: TradedFacilityParams,
): string => {
  const amounts = tradedFacility.amounts ?? []
  const last = amounts.length > 0 ? amounts[amounts.length - 1] : undefined
  return last?.amount ?? tradedFacility.tradeAmount
}

/**
 * The number DAML encodes in a system id — `extractNumericPart` in
 * SystemIds.daml splits on '-' and keeps the tail, so 'F-12' is 12.
 */
const systemIdNumber = (value: string | null | undefined): string | null => {
  const parts = (value ?? '').trim().split('-')
  if (parts.length !== 2) return null
  const tail = parts[1].trim()
  return tail.length > 0 ? tail.replace(/^0+(?=\d)/, '') : null
}

/**
 * Finds the AA row describing a traded facility.
 *
 * The row's `facilityId` cannot be compared as a string: DAML writes it as
 * `<prefix>-<extractNumericPart facility.systemId>`, and the prefix is the
 * TRADE's (the create path uses tradeIdPrefix, the update path hardcodes 'BX'),
 * never the facility's own. So 'BX-12' and facility systemId 'F-12' are the
 * same facility. Comparing the number behind the prefix is therefore the most
 * reliable match available — it comes from the same field DAML wrote the row
 * from — and it is the only one that works when a trade has several facilities,
 * where the single-row fallback cannot apply.
 */
export const findAaFacilityRow = (
  aaFacilities: AAFacilityDetails[],
  facility: FacilityParams | undefined,
  tradedFacilityCount: number,
): AAFacilityDetails | undefined => {
  if (aaFacilities.length === 0) return undefined

  if (facility) {
    const facilityNumber = systemIdNumber(facility.systemId)
    if (facilityNumber) {
      const bySystemId = aaFacilities.find(
        (row) => systemIdNumber(row.facilityId) === facilityNumber,
      )
      if (bySystemId) return bySystemId
    }

    const byLxId = aaFacilities.find(
      row =>
        normalize(row.facilityLxId) &&
        normalize(row.facilityLxId) === normalize(facility.lxId),
    )
    if (byLxId) return byLxId

    const byCusip = aaFacilities.find(
      row =>
        normalize(row.facilityCusip) &&
        normalize(row.facilityCusip) === normalize(facility.cusip),
    )
    if (byCusip) return byCusip

    const byName = aaFacilities.find(
      row =>
        normalize(row.facilityName) &&
        normalize(row.facilityName) === normalize(facility.name),
    )
    if (byName) return byName
  }

  if (aaFacilities.length === 1 && tradedFacilityCount === 1) {
    return aaFacilities[0]
  }

  return undefined
}

/** One facility whose document amount the trade has moved past. */
export type NotRefreshedFacility = {
  facilityName: string
  currency: string
  /** What the document records. */
  documentAmount: string
  /** What the trade says now. */
  tradeAmount: string
}

/** The trade date the document records vs the one the trade now carries. */
export type NotRefreshedTradeDate = {
  documentValue: string
  tradeValue: string
}

export type NotRefreshedDetail = {
  subTradeId: string
  /** When the document was last written — DAML bumps `createdAt` on every
   * regeneration, so this is the last time a refresh actually succeeded. */
  lastRefreshedAt: string | null
  facilities: NotRefreshedFacility[]
  /** Set when the document's trade date no longer matches the trade — a
   * booking edit whose regeneration did not land. */
  tradeDate: NotRefreshedTradeDate | null
}

/**
 * The document's trade date against the trade's own, when they disagree.
 *
 * DAML copies `tradeDate = masterTrade.tradeDate` into the metadata when the
 * document is written, so a booking edit that changes the trade date leaves the
 * two out of step until a refresh lands — the same staleness the amounts show,
 * on a field a paydown never touches. Compared by day: either side may arrive
 * as a plain date or a full timestamp.
 */
export const findNotRefreshedTradeDate = (
  aaDocument: AADocumentMetaData,
  masterTradeDate: string | null | undefined,
): NotRefreshedTradeDate | null => {
  const documentValue = aaDocument.tradeDate
  if (!documentValue || !masterTradeDate) return null

  const documentDate = moment(documentValue)
  const tradeDate = moment(masterTradeDate)
  if (!documentDate.isValid() || !tradeDate.isValid()) return null
  if (documentDate.isSame(tradeDate, 'day')) return null

  return { documentValue, tradeValue: masterTradeDate }
}

/**
 * Facilities on this AA document recording an amount the trade has since moved
 * past. Anything that cannot be compared — an unmatched row, a missing or
 * non-numeric amount — is skipped rather than guessed at, so a gap in the data
 * reads as "no warning", never as a false one.
 */
export const findNotRefreshedFacilities = (
  aaDocument: AADocumentMetaData,
  tradedFacilities: TradedFacilityParams[],
  facilityById: Map<string, FacilityParams>,
): NotRefreshedFacility[] => {
  const aaFacilities = aaDocument.facilities ?? []
  if (aaFacilities.length === 0 || tradedFacilities.length === 0) return []

  const result: NotRefreshedFacility[] = []

  for (const tradedFacility of tradedFacilities) {
    const facility = facilityById.get(tradedFacility.facilityId?.id ?? '')
    const aaRow = findAaFacilityRow(
      aaFacilities,
      facility,
      tradedFacilities.length,
    )
    if (!aaRow) continue

    const documentAmount = Number(aaRow.currentAmount)
    const tradeAmount = Number(currentTradedFacilityAmount(tradedFacility))
    if (
      aaRow.currentAmount === null ||
      aaRow.currentAmount === undefined ||
      aaRow.currentAmount === '' ||
      Number.isNaN(documentAmount) ||
      Number.isNaN(tradeAmount)
    ) {
      continue
    }

    if (Math.abs(documentAmount - tradeAmount) > AMOUNT_EPSILON) {
      result.push({
        facilityName: aaRow.facilityName || facility?.name || '',
        currency: aaRow.currency || '',
        documentAmount: String(aaRow.currentAmount),
        tradeAmount: currentTradedFacilityAmount(tradedFacility),
      })
    }
  }

  return result
}

/** True when any facility on this AA document is behind the trade. */
export const isAaDocumentNotRefreshed = (
  aaDocument: AADocumentMetaData,
  tradedFacilities: TradedFacilityParams[],
  facilityById: Map<string, FacilityParams>,
): boolean =>
  findNotRefreshedFacilities(aaDocument, tradedFacilities, facilityById)
    .length > 0

type SubTradeLike = { id?: string; masterTradeId: { id: string } }

type MasterTradeLike = { id?: string; tradeDate?: string | null }

/**
 * Per-sub-trade detail of what is out of date, keyed by sub-trade StableKey id
 * — which is what the Documents grid works in. Carries enough to tell the user
 * which facility is wrong, by how much, and when the document was last written.
 */
export const buildNotRefreshedDetailsBySubTradeId = (
  subTrades: SubTradeLike[],
  tradedFacilities: TradedFacilityParams[],
  aaDocuments: AADocumentMetaData[],
  facilities: FacilityParams[],
  masterTrades: MasterTradeLike[] = [],
): Map<string, NotRefreshedDetail> => {
  const result = new Map<string, NotRefreshedDetail>()
  if (aaDocuments.length === 0) return result

  // AA metadata joins on the sub-trade StableKey id in signableDocumentid._2 —
  // `subTradeId` on the document is the human systemId and does not match.
  const aaBySubTradeId = new Map<string, AADocumentMetaData>()
  for (const doc of aaDocuments) {
    const subTradeKeyId = doc.signableDocumentid?._2
    if (subTradeKeyId) aaBySubTradeId.set(subTradeKeyId, doc)
  }

  const tradedFacilitiesBySubTradeId = new Map<string, TradedFacilityParams[]>()
  for (const tf of tradedFacilities) {
    const subTradeId = tf.contractId?.id
    if (!subTradeId) continue
    if (!tradedFacilitiesBySubTradeId.has(subTradeId)) {
      tradedFacilitiesBySubTradeId.set(subTradeId, [])
    }
    tradedFacilitiesBySubTradeId.get(subTradeId)!.push(tf)
  }

  const facilityById = new Map<string, FacilityParams>()
  for (const facility of facilities) {
    if (facility.id) facilityById.set(facility.id, facility)
  }

  const tradeDateByMasterTradeId = new Map<string, string | null>()
  for (const masterTrade of masterTrades) {
    if (masterTrade.id) {
      tradeDateByMasterTradeId.set(masterTrade.id, masterTrade.tradeDate ?? null)
    }
  }


  for (const subTrade of subTrades) {
    const subTradeId = subTrade.id ?? ''
    const aaDocument = aaBySubTradeId.get(subTradeId)
    if (!aaDocument) {
      continue
    }

    const notRefreshedFacilities = findNotRefreshedFacilities(
      aaDocument,
      tradedFacilitiesBySubTradeId.get(subTradeId) ?? [],
      facilityById,
    )
    const notRefreshedTradeDate = findNotRefreshedTradeDate(
      aaDocument,
      tradeDateByMasterTradeId.get(subTrade.masterTradeId?.id ?? ''),
    )

    // Either signal on its own means the document was not refreshed: a paydown
    // moves the amounts, a booking edit moves the trade date.

    if (notRefreshedFacilities.length > 0 || notRefreshedTradeDate) {
      result.set(subTradeId, {
        subTradeId,
        lastRefreshedAt: aaDocument.createdAt ?? null,
        facilities: notRefreshedFacilities,
        tradeDate: notRefreshedTradeDate,
      })
    }
  }

  return result
}
